import * as React from 'react'
import type { TranscriptUtterance } from '@/client/types.gen'

export interface UseFilteredUtterancesParams {
	utterances: TranscriptUtterance[] | null
	editedUtterances: Map<number, string>
	editedSpeakers: Map<number, string>
	selectedSpeakers: Set<string>
	speakerFilterActive: boolean
}

export interface UseFilteredUtterancesReturn {
	filteredSearchableContent: string
	filteredUtterances: TranscriptUtterance[]
	filteredEditedUtterances: Map<number, string>
	filteredEditedSpeakers: Map<number, string>
	fullIndexByFilteredIndex: number[]
}

const EMPTY_RESULT: UseFilteredUtterancesReturn = {
	filteredSearchableContent: '',
	filteredUtterances: [],
	filteredEditedUtterances: new Map<number, string>(),
	filteredEditedSpeakers: new Map<number, string>(),
	fullIndexByFilteredIndex: [],
}

export function useFilteredUtterances({
	utterances,
	editedUtterances,
	editedSpeakers,
	selectedSpeakers,
	speakerFilterActive,
}: UseFilteredUtterancesParams): UseFilteredUtterancesReturn {
	return React.useMemo(() => {
		if (!utterances?.length || !speakerFilterActive) {
			return EMPTY_RESULT
		}
		const filtered: { utterance: TranscriptUtterance; fullIndex: number }[] = []
		for (let i = 0; i < utterances.length; i++) {
			const u = utterances[i]
			const currentSpeaker = editedSpeakers.get(i) ?? u.speaker
			if (selectedSpeakers.has(currentSpeaker)) {
				filtered.push({ utterance: u, fullIndex: i })
			}
		}
		const filteredUtterancesList = filtered.map((f) => f.utterance)
		const fullIndexByFilteredIndexList = filtered.map((f) => f.fullIndex)
		const filteredSearchableContentStr = filtered
			.map(({ utterance, fullIndex }) => {
				const speaker = editedSpeakers.get(fullIndex) ?? utterance.speaker
				const text = editedUtterances.get(fullIndex) ?? utterance.text
				return `${speaker}: ${text}`
			})
			.join('\n')
		const filteredEditedUtterancesMap = new Map<number, string>()
		const filteredEditedSpeakersMap = new Map<number, string>()
		for (let i = 0; i < fullIndexByFilteredIndexList.length; i++) {
			const fullIdx = fullIndexByFilteredIndexList[i]
			const t = editedUtterances.get(fullIdx)
			if (t !== undefined) filteredEditedUtterancesMap.set(i, t)
			const s = editedSpeakers.get(fullIdx)
			if (s !== undefined) filteredEditedSpeakersMap.set(i, s)
		}
		return {
			filteredSearchableContent: filteredSearchableContentStr,
			filteredUtterances: filteredUtterancesList,
			filteredEditedUtterances: filteredEditedUtterancesMap,
			filteredEditedSpeakers: filteredEditedSpeakersMap,
			fullIndexByFilteredIndex: fullIndexByFilteredIndexList,
		}
	}, [utterances, speakerFilterActive, selectedSpeakers, editedUtterances, editedSpeakers])
}
